import { isNativePlatform } from '@lib/platform';

/**
 * Convert a VAPID public key from URL-safe base64 to a Uint8Array
 * for use with PushManager.subscribe().
 *
 * Returns `Uint8Array<ArrayBuffer>` rather than plain `Uint8Array`: since
 * TS 5.7 the latter widens to `Uint8Array<ArrayBufferLike>`, which includes
 * SharedArrayBuffer and so no longer satisfies `BufferSource` on
 * `applicationServerKey`. The array is always allocated here, so the
 * narrower type is accurate.
 */
export const urlBase64ToUint8Array = (base64String: string): Uint8Array<ArrayBuffer> => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/**
 * Convert a buffer (e.g. from PushSubscription.options.applicationServerKey)
 * back to a URL-safe base64 string for comparison with a stored VAPID key.
 *
 * Accepts BufferSource so callers don't need to coerce TypedArrays — older
 * browser implementations may return a typed array view rather than a fresh
 * ArrayBuffer.
 */
export const arrayBufferToUrlBase64 = (buffer: BufferSource): string => {
  const bytes = ArrayBuffer.isView(buffer)
    ? new Uint8Array(buffer.buffer, buffer.byteOffset, buffer.byteLength)
    : new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

/**
 * Normalize a base64 string to URL-safe, no-padding form.
 *
 * Use to compare a server-provided VAPID public key against the form produced
 * by arrayBufferToUrlBase64 — guards against an operator pasting a key with
 * `+`/`/`/`=` that would otherwise look like a permanent mismatch and trigger
 * an infinite rotation loop.
 */
export const normalizeUrlBase64 = (s: string): string =>
  s.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');

/**
 * Detect the browser name from the user agent string.
 * Used as metadata when registering push subscriptions.
 */
export const detectBrowser = (): string => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Unknown';
};

/**
 * Check if the Web Push API is supported in this environment.
 * Returns false on native platforms (which use FCM via Capacitor).
 */
export const isWebPushSupported = (): boolean => {
  if (isNativePlatform()) return false;
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};
